import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icon,
  IconButton,
  Link,
  Tooltip,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import OrderProductTatInsightsPopup from "../order/OrderProductTatInsightsPopup";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { OrderProduct } from "../../../../../models/Order/OrderProduct";
import {
  OrderProductDeleteCheckPo,
  OrderProductDeleteCheckResult,
} from "../../../../../models/PurchaseRequest/OrderProductDeleteCheck";
import { observer, useLocalObservable } from "mobx-react";
import pencil_uderline from "../../../../../assets/pencil_uderline.png";
import SingleInput from "../../../../../views/components/common/pop-up/SingleInput";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { GstPercentage } from "../../../../../models/Order/OrderStatus";
import dayjs from "dayjs";
import { useStores } from "../../../use-stores";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import {
  getStockCheckRequestStatusLabel,
  StockCheckRequestStatus,
} from "../../../../../models/Stock/Stock";
import { OrderProfitLossStatement } from "../../../../../models/Order/OrderProfitLossStatement";
import PercentageStatCard from "../../../../../views/components/common/cards/PercentageStatCard";
import OrderProductVariantsDrawer, {
  ProductVariantRowInput,
} from "../order/OrderProductVariantsDrawer";
import OrderProductInventoryAvailabilityPopup from "../order/OrderProductInventoryAvailabilityPopup";
import { useNotification } from "../../../../../utils/NotificationsContext";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import { StockIssueRequestStore } from "../../../stores/StockIssueRequestStore";
import { OrderProductInventoryAvailability } from "../../../../../models/StockIssueRequest/OrderInventoryAvailability";
import { OrderType } from "../../../../../models/Order/Order";
import {
  availabilityToneColor,
  getProductInventoryAvailabilityDisplay,
} from "../../../helpers/orderInventoryAvailability.helper";
import {
  getUnpaidSampleOrderHintMessage,
  shouldShowUnpaidSampleOrderHint,
} from "../../../helpers/unpaidSampleSourcing.helper";

const getPurchaseRequestDetailsPath = (prId: number, orderId?: number) =>
  `/purchase/requests/details?prId=${prId}${
    orderId ? `&projectId=${orderId}` : ""
  }`;

const LinkedIdList: React.FC<{
  items: Array<{ id: number; prId?: number }>;
  orderId?: number;
}> = ({ items, orderId }) => (
  <>
    {items.map((item, index) => (
      <span key={`${item.prId || "pr"}-${item.id}`}>
        {index > 0 ? ", " : null}
        <Link
          component={RouterLink}
          to={getPurchaseRequestDetailsPath(item.prId || item.id, orderId)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{ fontWeight: 700 }}
        >
          #{item.id}
        </Link>
      </span>
    ))}
  </>
);

/* ─── Metric Cell ─────────────────────────────────────────────────── */

interface MetricCellProps {
  label: string;
  value: React.ReactNode;
  onEdit?: () => void;
}

const MetricCell: React.FC<MetricCellProps> = ({ label, value, onEdit }) => (
  <Box
    sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 68 }}
  >
    <Box
      component="span"
      sx={{
        fontSize: "0.63rem",
        color: "#94A3B8",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      <Box
        component="span"
        sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#334155" }}
      >
        {value}
      </Box>
      {onEdit && (
        <IconButton
          size="small"
          onClick={onEdit}
          sx={{
            width: 16,
            height: 16,
            p: 0.25,
            "&:hover": { backgroundColor: "transparent" },
            "&:active": { backgroundColor: "transparent" },
          }}
        >
          <Icon sx={{ fontSize: "12px" }}>
            <img
              src={pencil_uderline}
              alt="edit-icon"
              style={{ width: 10, height: 10 }}
            />
          </Icon>
        </IconButton>
      )}
    </Box>
  </Box>
);

/* ─── Main Component ──────────────────────────────────────────────── */

interface OrderProductsProps {
  order_id?: number;
  products?: OrderProduct[];
  profit_loss_statement?: OrderProfitLossStatement;
  padding?: string;
  boxShadow?: string;
  updateQuantity?: (id: number, value: number, field: string) => void;
  handleDeleteProduct: (id: number) => void | Promise<unknown>;
  toggleAddProductsPopup: () => void;
  canGiveDiscount?: boolean;
  canUpdateOfferPrice?: boolean;
  canUpdateSellingPrice?: boolean;
  canChangeQuantity?: boolean;
  canDeleteProduct?: boolean;
  canAddProduct?: boolean;
  canSeeTaxes?: boolean;
  canSeeAmount?: boolean;
  handleViewImagesOfProduct?: (product: OrderProduct) => void;
  handleViewMockupOfProduct?: (product: OrderProduct) => void;
  handleUpdateAllBtnClick?: (value: boolean) => void;
  isSez?: boolean;
  /** Highlights card background when order validation requires line items */
  validationErrorBackground?: boolean;
  /** Bumps when a SIR is created so product hint can refresh */
  sirRefreshKey?: number;
}

const OrderProducts: React.FC<OrderProductsProps> = ({
  order_id,
  products,
  profit_loss_statement,
  padding,
  boxShadow,
  updateQuantity,
  handleDeleteProduct,
  toggleAddProductsPopup,
  canGiveDiscount = true,
  canUpdateOfferPrice = true,
  canUpdateSellingPrice = true,
  canChangeQuantity = true,
  canDeleteProduct = true,
  canAddProduct = true,
  canSeeTaxes = true,
  canSeeAmount = true,
  handleViewImagesOfProduct,
  handleViewMockupOfProduct,
  handleUpdateAllBtnClick,
  isSez = false,
  validationErrorBackground = false,
  sirRefreshKey = 0,
}) => {
  const theme = useTheme();
  const { userStore, productStore, orderStore, warehouseStore } =
    useStores();
  const { showError } = useNotification();
  const stockIssueRequestStore = useRef(new StockIssueRequestStore()).current;
  const deleteInProgressRef = useRef(false);
  const deleteUi = useLocalObservable(() => ({
    productPendingDelete: null as { id: number; name: string } | null,
    isDeletingProduct: false,
    productDeleteBlock: null as OrderProductDeleteCheckResult | null,
  }));

  const resolveWarehouseName = (warehouseId: string): string => {
    const warehouse = warehouseStore.warehouses.find(
      (item) => String(item.id) === String(warehouseId),
    );
    return warehouse?.identifier || warehouseId;
  };

  const [availabilityPopupOpen, setAvailabilityPopupOpen] = useState(false);
  const [
    selectedProductForAvailability,
    setSelectedProductForAvailability,
  ] = useState<OrderProduct | null>(null);

  useEffect(() => {
    if (!order_id || warehouseStore.warehouses.length > 0) return;
    warehouseStore.searchWarehouse().catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id]);

  useEffect(() => {
    if (!order_id || !products || products.length === 0) {
      stockIssueRequestStore.clearOrderInventoryAvailability();
      return;
    }

    stockIssueRequestStore
      .loadOrderProductInventoryTotals(products)
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
          "Failed to fetch Swageazy inventory availability.",
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id, products?.length]);

  const getProductAvailability = (
    productId: number,
  ): OrderProductInventoryAvailability | null =>
    stockIssueRequestStore.orderInventoryAvailabilityByProductId[productId] ??
    null;

  const formatAvailableQty = (productId: number): string => {
    return getProductInventoryAvailabilityDisplay({
      availability: getProductAvailability(productId),
      isLoading: stockIssueRequestStore.isOrderInventoryTotalsLoading,
    }).shortLabel;
  };

  const getAvailabilityDisplay = (productId: number) =>
    getProductInventoryAvailabilityDisplay({
      availability: getProductAvailability(productId),
      isLoading: stockIssueRequestStore.isOrderInventoryTotalsLoading,
    });

  const orderTypeId =
    orderStore.existingOrder?.type_id ?? orderStore.orderRequest?.type_id;
  const isUnpaidSampleOrder =
    Number(orderTypeId) === OrderType.UNPAID_SAMPLE;
  const isPodOrder = Number(orderTypeId) === OrderType.POD;

  const requestDeleteProduct = (product: OrderProduct) => {
    if (deleteInProgressRef.current) return;
    deleteUi.productPendingDelete = {
      id: product.id,
      name: product.name || "this product",
    };
  };

  const handleCancelDeleteProduct = () => {
    if (deleteInProgressRef.current) return;
    deleteUi.productPendingDelete = null;
  };

  const handleConfirmDeleteProduct = async () => {
    if (!deleteUi.productPendingDelete || deleteInProgressRef.current) return;

    const productId = deleteUi.productPendingDelete.id;
    deleteUi.productPendingDelete = null;

    if (!order_id) {
      handleDeleteProduct(productId);
      return;
    }

    deleteInProgressRef.current = true;
    deleteUi.isDeletingProduct = true;
    try {
      await handleDeleteProduct(productId);
    } catch (error: any) {
      const check = error?.response?.data?.result as
        | OrderProductDeleteCheckResult
        | undefined;
      const hasBlockingIds =
        (check?.pending_purchase_requests?.length || 0) > 0 ||
        (check?.unclosed_purchase_orders?.length || 0) > 0 ||
        (check?.purchase_requests_without_closed_po?.length || 0) > 0;
      if (check && check.can_delete === false && hasBlockingIds) {
        deleteUi.productDeleteBlock = check;
        return;
      }
      showError(
        "Cannot delete product",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete this product.",
      );
    } finally {
      deleteInProgressRef.current = false;
      deleteUi.isDeletingProduct = false;
    }
  };

  useEffect(() => {
    if (!order_id || !isUnpaidSampleOrder) {
      return;
    }

    stockIssueRequestStore
      .getStockIssueRequests({
        order_id: String(order_id),
        sir_flow: "sample",
        page_no: 1,
        limit: 50,
        sort_by: "created_date",
        sort_order: "desc",
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order_id, isUnpaidSampleOrder, sirRefreshKey]);

  const sampleSirRequests = stockIssueRequestStore.stockIssueRequests.filter(
    (req) => req.sir_flow === "sample",
  );

  const showUnpaidSampleHint =
    isUnpaidSampleOrder &&
    shouldShowUnpaidSampleOrderHint({
      orderId: order_id,
      products: products ?? [],
      sampleSirRequests,
    });

  const unpaidSampleStockSummary = (() => {
    if (!products?.length || !order_id) {
      return { anyInWarehouse: false, allLinesCovered: false, isLoading: true };
    }
    if (stockIssueRequestStore.isOrderInventoryTotalsLoading) {
      return { anyInWarehouse: false, allLinesCovered: false, isLoading: true };
    }

    let anyInWarehouse = false;
    let allLinesCovered = true;

    for (const product of products) {
      const availability = getProductAvailability(product.id);
      if (!availability) {
        allLinesCovered = false;
        continue;
      }
      const available = Number(availability.total_available_quantity ?? 0);
      const ordered = Number(availability.ordered_quantity ?? 0);
      if (available > 0) anyInWarehouse = true;
      if (ordered > 0 && available < ordered) allLinesCovered = false;
    }

    return { anyInWarehouse, allLinesCovered, isLoading: false };
  })();

  const openAvailabilityPopup = (product: OrderProduct) => {
    setSelectedProductForAvailability(product);
    setAvailabilityPopupOpen(true);

    if (!order_id) return;

    stockIssueRequestStore
      .loadOrderProductSkuAvailability(order_id, product)
      .catch((err) => {
        showError(
          "Error",
          err?.response?.data?.message ||
          "Failed to fetch SKU-level inventory availability.",
        );
      });
  };

  const closeAvailabilityPopup = () => {
    setAvailabilityPopupOpen(false);
    setSelectedProductForAvailability(null);
  };

  const handleProductTatInsightsClick = () => {
    if (!order_id) return;
    orderStore
      .fetchProductTatInsights(order_id)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
          "Failed to fetch product TAT insights.",
        ),
      );
  };

  const [editableField, setEditableField] = useState<{
    productId: number | null;
    field: string | null;
  }>({
    productId: null,
    field: null,
  });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState("");
  const [isVariantsDrawerOpen, setIsVariantsDrawerOpen] = useState(false);
  const [selectedProductForVariants, setSelectedProductForVariants] =
    useState<OrderProduct | null>(null);
  const [productVariantRows, setProductVariantRows] = useState<
    Record<number, ProductVariantRowInput[]>
  >({});
  const [variantsCountByProductId, setVariantsCountByProductId] = useState<
    Record<number, number>
  >({});

  const getVariantsCount = (product: OrderProduct) =>
    variantsCountByProductId[product.id] ?? product.variants_count ?? 0;

  const formatVariantsCtaLabel = (count: number) => {
    if (count <= 0) return "Add Variant";
    if (count === 1) return "1 Variant";
    return `${count} Variants`;
  };

  const handleEditClick = (
    productId: number,
    field: string,
    currentValue: string,
  ) => {
    setEditableField({ productId, field });
    setModalValue(currentValue);
    setModalOpen(true);
  };

  const handleModalSubmit = (value: string) => {
    if (!editableField.productId || !editableField.field) return;

    if (editableField.field) {
      updateField(editableField.productId, Number(value), editableField.field);
    }

    setModalOpen(false);
    setEditableField({ productId: null, field: null });
  };

  const updateField = (productId: number, value: number, field: string) => {
    if (updateQuantity) updateQuantity(productId, value, field);
  };

  const openVariantsDrawer = (product: OrderProduct) => {
    setSelectedProductForVariants(product);
    setIsVariantsDrawerOpen(true);
  };

  const closeVariantsDrawer = () => {
    setIsVariantsDrawerOpen(false);
    setSelectedProductForVariants(null);
  };

  const handleVariantsSubmit = (variantRows: ProductVariantRowInput[]) => {
    if (!selectedProductForVariants) return;

    setProductVariantRows((prev) => ({
      ...prev,
      [selectedProductForVariants.id]: variantRows,
    }));
  };

  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());
  };

  const isDateMoreThanThreeMonthsOld = (date: Date | undefined): boolean => {
    if (!date) return false;

    const lastSoldDate = dayjs(date);
    const currentDate = dayjs();
    const twoMonthsAgo = currentDate.subtract(2, "month");

    return lastSoldDate.isValid() ? lastSoldDate.isBefore(twoMonthsAgo) : false;
  };

  const getStockCheckStatusChip = (
    stockCheck: StockCheckRequestStatus | null | undefined,
  ) => {
    let color: string = theme.palette.customColors.info;

    const status = getStockCheckRequestStatusLabel(stockCheck);

    if (stockCheck === StockCheckRequestStatus.CLOSED) {
      color = theme.palette.customColors.successGreen;
    } else if (stockCheck === StockCheckRequestStatus.REJECTED) {
      color = theme.palette.customColors.errorRed;
    }
    return (
      <CustomTypography variant="body3" fontWeight={700} color={color}>
        {status}
      </CustomTypography>
    );
  };

  return (
    <Box sx={{ borderRadius: 2 }}>
      <Card
        sx={{
          p: padding || { xs: 2, md: 3 },
          borderRadius: "12px",
          border: `1px solid ${theme.palette.customColors.lightGray}`,
          boxShadow:
            boxShadow ||
            `0 2px 8px ${theme.palette.customColors.shadowLightBorder}`,
          ...(validationErrorBackground && {
            border: `1px solid ${theme.palette.error.main}`,
          }),
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <CustomTypography variant="heading4" fontWeight={700}>
            Products Info
          </CustomTypography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {order_id && (
              <Tooltip title="Product TAT Insights">
                <span>
                  <IconButton
                    onClick={handleProductTatInsightsClick}
                    disabled={orderStore.isProductTatInsightsLoading}
                    sx={{
                      color: theme.palette.primary.main,
                      backgroundColor: theme.palette.primary.main + "10",
                      width: 32,
                      height: 32,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main + "20",
                      },
                    }}
                    size="small"
                  >
                    {orderStore.isProductTatInsightsLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <TimelineOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canAddProduct && (
              <Box sx={{ display: "flex", gap: 4 }}>
                <AppButton
                  label="Update All Quantities"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (handleUpdateAllBtnClick) handleUpdateAllBtnClick(true);
                  }}
                  customStyles={{
                    backgroundColor: "transparent",
                    padding: 1,
                    margin: 0,
                    fontSize: "14px",
                    color: theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.customColors.pinkDark,
                      boxShadow: "none",
                    },
                  }}
                />
                <AppButton
                  label="Add Products"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    toggleAddProductsPopup();
                  }}
                  customStyles={{
                    backgroundColor: "transparent",
                    padding: 1,
                    margin: 0,
                    fontSize: "14px",
                    color: theme.palette.customColors.pinkDark,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: theme.palette.customColors.pinkDark,
                      boxShadow: "none",
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        {isPodOrder ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.customColors?.softBorder || theme.palette.divider}`,
              backgroundColor:
                theme.palette.customColors?.blueLightBackground ||
                theme.palette.action.hover,
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color={
                theme.palette.customColors?.darkBlueText ||
                theme.palette.text.primary
              }
              sx={{ fontSize: "0.85rem", lineHeight: 1.5 }}
            >
              Note: On POD orders, adding, removing, or changing product
              quantity before Print Outward moves the entire order back to
              Unassigned and returns allocated stock. Changes are not allowed
              after Print Outward.
            </CustomTypography>
          </Box>
        ) : null}
        {showUnpaidSampleHint ? (
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              border: "1px solid #BFDBFE",
              backgroundColor: "#EFF6FF",
            }}
          >
            <CustomTypography
              variant="body3"
              fontWeight={700}
              color="#1E40AF"
              sx={{ fontSize: "0.78rem", mb: 0.5 }}
            >
              Unpaid sample order
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color="#1E3A8A"
              sx={{ fontSize: "0.72rem", lineHeight: 1.5 }}
            >
              {getUnpaidSampleOrderHintMessage({
                orderId: order_id,
                products: products ?? [],
                sampleSirRequests,
                isInventoryLoading:
                  stockIssueRequestStore.isOrderInventoryTotalsLoading,
                anyInWarehouse: unpaidSampleStockSummary.anyInWarehouse,
                allLinesCovered: unpaidSampleStockSummary.allLinesCovered,
              })}
            </CustomTypography>
          </Box>
        ) : null}

        {/* Product Cards */}
        {!products || products.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={700}
              color="rgba(218, 218, 218, 1)"
            >
              NO PRODUCTS FOUND!
            </CustomTypography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {products.map((product, index) => {
              const variantsCount = getVariantsCount(product);
              const discountPct =
                product.discount && product.unit_price
                  ? ((product.discount / product.unit_price) * 100) % 1 === 0
                    ? `${(product.discount / product.unit_price) * 100}`
                    : `${((product.discount / product.unit_price) * 100).toFixed(2)}`
                  : "0";

              const tax =
                isSez === false &&
                  product.selling_price &&
                  product.gst_percentage
                  ? (
                    (product.selling_price *
                      product.gst_percentage *
                      product.quantity) /
                    100
                  ).toFixed(2)
                  : "0";

              const lastSoldProduct = productStore?.lastSoldProducts?.find(
                (prod) => Number(prod.product_id) === Number(product.id),
              );

              const lastPurchaseColor =
                !lastSoldProduct || !lastSoldProduct.date
                  ? theme.palette.customColors.info
                  : isDateMoreThanThreeMonthsOld(lastSoldProduct.date)
                    ? theme.palette.customColors.errorRed
                    : theme.palette.customColors.successGreen;

              const lastPurchaseText = lastSoldProduct?.date
                ? `${formatDateWithoutTime(lastSoldProduct.date)} (₹${lastSoldProduct.rate ?? "0"})`
                : "No date available";

              return (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    transition: "box-shadow 0.15s",
                    "&:hover": {
                      boxShadow: "0 2px 12px rgba(15,23,42,0.08)",
                    },
                  }}
                >
                  {/* Card header: image + info + actions */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      px: 2,
                      pt: 1.5,
                      pb: 1.25,
                      gap: 1.5,
                      flexWrap: { xs: "wrap", sm: "nowrap" },
                    }}
                  >
                    {/* Index badge */}
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "6px",
                        backgroundColor: "#F1F5F9",
                        color: "#64748B",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        mt: 0.5,
                      }}
                    >
                      {index + 1}
                    </Box>

                    {/* Product image with hover overlay */}
                    <Box
                      sx={{ position: "relative", flexShrink: 0 }}
                      onMouseEnter={() => setHoverIndex(index)}
                      onMouseLeave={() => setHoverIndex(null)}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "8px",
                          backgroundColor: "rgba(242, 242, 242, 1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={product.image_url}
                          alt={product.name}
                          style={{
                            width: 38,
                            height: 38,
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                      {hoverIndex === index && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "#00000050",
                            borderRadius: "8px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => handleViewImagesOfProduct!(product)}
                        >
                          <VisibilityOutlinedIcon
                            sx={{ color: "#fff", fontSize: 16 }}
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Product name + meta */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                          mb: 0.25,
                        }}
                      >
                        <CustomTypography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: "#1E293B" }}
                        >
                          {product.name}
                        </CustomTypography>
                        <Link
                          href={`/products/${product.id}`}
                          target="_blank"
                          style={{ textDecoration: "none" }}
                        >
                          <Chip
                            label={`#${product.id}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              backgroundColor: "#F1F5F9",
                              color: "#64748B",
                              borderRadius: "4px",
                              cursor: "pointer",
                              "& .MuiChip-label": { px: 0.75 },
                              "&:hover": { backgroundColor: "#E2E8F0" },
                            }}
                          />
                        </Link>
                        {product.has_psd_templates === 1 ? (
                          <Tooltip title="PSD templates configured">
                            <Chip
                              label="PSD"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                backgroundColor: theme.palette.red.dark + "30",
                                color: theme.palette.red.dark,
                                borderRadius: "4px",
                                "& .MuiChip-label": { px: 0.75 },
                              }}
                            />
                          </Tooltip>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CustomTypography
                            variant="body3"
                            color="#94A3B8"
                            sx={{ fontSize: "0.71rem" }}
                          >
                            Last Purchase:
                          </CustomTypography>
                          <CustomTypography
                            variant="body3"
                            fontWeight={700}
                            color={lastPurchaseColor}
                            sx={{ fontSize: "0.71rem" }}
                          >
                            {lastPurchaseText}
                          </CustomTypography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <CustomTypography
                            variant="body3"
                            color="#94A3B8"
                            sx={{ fontSize: "0.71rem" }}
                          >
                            Stock:
                          </CustomTypography>
                          {getStockCheckStatusChip(product.stock_check_status)}
                        </Box>
                        {order_id && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <CustomTypography
                              variant="body3"
                              color="#94A3B8"
                              sx={{ fontSize: "0.71rem" }}
                            >
                              Warehouse:
                            </CustomTypography>
                            <Tooltip
                              title={
                                getAvailabilityDisplay(product.id).tooltip
                              }
                            >
                              <Box
                                component="button"
                                type="button"
                                onClick={() => openAvailabilityPopup(product)}
                                disabled={
                                  stockIssueRequestStore.isOrderInventoryTotalsLoading
                                }
                                sx={{
                                  border: "none",
                                  background: "transparent",
                                  padding: 0,
                                  cursor: "pointer",
                                  fontSize: "0.71rem",
                                  fontWeight: 700,
                                  color: availabilityToneColor(
                                    getAvailabilityDisplay(product.id).tone,
                                  ),
                                  textDecoration: "underline",
                                  "&:disabled": {
                                    cursor: "default",
                                    color: "#94A3B8",
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                {formatAvailableQty(product.id)}
                              </Box>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Actions */}
                    {order_id && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexShrink: 0,
                          flexWrap: { xs: "wrap", sm: "nowrap" },
                          justifyContent: "flex-end",
                        }}
                      >
                        <AppButton
                          label={formatVariantsCtaLabel(variantsCount)}
                          onClick={() => openVariantsDrawer(product)}
                          customStyles={{
                            backgroundColor: variantsCount
                              ? "#EEF2FF"
                              : "#FDF2F8",
                            color: variantsCount
                              ? "#4F46E5"
                              : theme.palette.customColors.pinkDark,
                            border: `1px solid ${variantsCount ? "#C7D2FE" : "#FBCFE8"
                              }`,
                            padding: "3px 10px",
                            minHeight: "28px",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none", opacity: 0.85 },
                          }}
                        />
                        <AppButton
                          label="Mockups"
                          onClick={() => handleViewMockupOfProduct!(product)}
                          customStyles={{
                            backgroundColor: "#F8FAFC",
                            color: theme.palette.customColors.darkBlue,
                            border: "1px solid #E2E8F0",
                            padding: "3px 10px",
                            minHeight: "28px",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            boxShadow: "none",
                            "&:hover": { boxShadow: "none", opacity: 0.85 },
                          }}
                        />
                        {canDeleteProduct && (
                          <IconButton
                            size="small"
                            onClick={() => requestDeleteProduct(product)}
                            disabled={deleteUi.isDeletingProduct}
                            sx={{
                              backgroundColor:
                                theme.palette.customColors.pinkLight,
                              width: 28,
                              height: 28,
                              borderRadius: "6px",
                              "&:hover": { backgroundColor: "#FCE7F3" },
                            }}
                          >
                            <DeleteIcon
                              sx={{
                                color: theme.palette.customColors.pinkDark,
                                width: 16,
                                height: 16,
                              }}
                            />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Metrics strip */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.25,
                      borderTop: "1px solid #F1F5F9",
                      backgroundColor: "#FAFAFA",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: { xs: 1.5, sm: 2.5 },
                      alignItems: "flex-start",
                    }}
                  >
                    <MetricCell
                      label="HSN"
                      value={product.hsn_code || "–"}
                      onEdit={
                        product.hsn_code && canUpdateOfferPrice
                          ? () =>
                            handleEditClick(
                              product.id,
                              "hsn",
                              String(product.hsn_code),
                            )
                          : undefined
                      }
                    />
                    <MetricCell
                      label="Orig Price"
                      value={product.product_selling_price || "–"}
                    />
                    <MetricCell
                      label="Offer Price"
                      value={product.unit_price}
                      onEdit={
                        canUpdateOfferPrice
                          ? () =>
                            handleEditClick(
                              product.id,
                              "offerPrice",
                              String(product.unit_price),
                            )
                          : undefined
                      }
                    />
                    <MetricCell
                      label="Discount"
                      value={product.discount || "–"}
                    />
                    <MetricCell
                      label="Disc %"
                      value={discountPct}
                      onEdit={
                        canGiveDiscount
                          ? () =>
                            handleEditClick(
                              product.id,
                              "discount%",
                              discountPct,
                            )
                          : undefined
                      }
                    />
                    {userStore.hasPermission(EmployeeRoles.ALL) && (
                      <MetricCell
                        label="Cost Price"
                        value={product.product_cost_price || "–"}
                      />
                    )}
                    <MetricCell
                      label="Selling Price"
                      value={product.selling_price}
                      onEdit={
                        canUpdateSellingPrice
                          ? () =>
                            handleEditClick(
                              product.id,
                              "sellingPrice",
                              String(product.selling_price),
                            )
                          : undefined
                      }
                    />
                    {userStore.hasPermission(EmployeeRoles.ALL) && (
                      <MetricCell
                        label="Margin (CP)"
                        value={product.margin_cp || "–"}
                      />
                    )}
                    {userStore.hasPermission(EmployeeRoles.ALL) && (
                      <MetricCell
                        label="Margin (PO)"
                        value={product.margin_po || "–"}
                      />
                    )}
                    <MetricCell
                      label="Qty"
                      value={product.quantity}
                      onEdit={
                        canChangeQuantity
                          ? () =>
                            handleEditClick(
                              product.id,
                              "quantity",
                              String(product.quantity),
                            )
                          : undefined
                      }
                    />
                    {canSeeTaxes && (
                      <>
                        <MetricCell
                          label="GST %"
                          value={
                            product.gst_percentage != null &&
                              product.gst_percentage !== undefined
                              ? product.gst_percentage === 0
                                ? "0"
                                : product.gst_percentage
                              : "–"
                          }
                          onEdit={
                            product.gst_percentage != null &&
                              product.gst_percentage !== undefined &&
                              canUpdateOfferPrice
                              ? () =>
                                handleEditClick(
                                  product.id,
                                  "gst",
                                  String(product.gst_percentage),
                                )
                              : undefined
                          }
                        />
                        <MetricCell label="Tax" value={tax} />
                      </>
                    )}
                    <MetricCell
                      label="MRP"
                      value={order_id ? product.mrp || "–" : "–"}
                    />
                    {canSeeAmount && (
                      <MetricCell
                        label="Amount"
                        value={
                          Math.round((product.amount + Number.EPSILON) * 100) /
                          100 || "–"
                        }
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {userStore.hasPermission(
          EmployeeRoles.VIEW_ORDER_PROFIT_MARGIN,
          true,
        ) &&
          profit_loss_statement && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <PercentageStatCard
                value={profit_loss_statement?.total_cp_product_margin}
                title="Profit Margin"
                isSecure
              />
            </Box>
          )}

        <SingleInput
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          heading={`Edit ${editableField.field ? formatFieldName(editableField.field) : ""
            }`}
          content={`Enter value`}
          optionalText={`${editableField.productId
            ? products?.find(
              (product) => product.id === editableField.productId,
            )?.name
            : ""
            }`}
          type={editableField.field === "gst" ? "select" : "number"}
          selectOptions={
            editableField.field === "gst" ? GstPercentage : undefined
          }
          initialValue={modalValue}
          onSubmit={handleModalSubmit}
        />
        <OrderProductVariantsDrawer
          open={isVariantsDrawerOpen}
          product_id={selectedProductForVariants?.id}
          order_id={order_id}
          total_quantity={selectedProductForVariants?.quantity || 0}
          productDetails={null}
          initialRows={
            selectedProductForVariants
              ? productVariantRows[selectedProductForVariants.id]
              : undefined
          }
          onClose={closeVariantsDrawer}
          onSubmit={handleVariantsSubmit}
          onVariantsCountChange={(count) => {
            if (!selectedProductForVariants) return;
            setVariantsCountByProductId((prev) => ({
              ...prev,
              [selectedProductForVariants.id]: count,
            }));
          }}
        />

        <OrderProductTatInsightsPopup
          open={orderStore.isProductTatInsightsOpen}
          insights={orderStore.productTatInsights}
          onClose={() => orderStore.closeProductTatInsightsPopup()}
        />

        <OrderProductInventoryAvailabilityPopup
          open={availabilityPopupOpen}
          onClose={closeAvailabilityPopup}
          productName={selectedProductForAvailability?.name}
          availability={
            selectedProductForAvailability
              ? getProductAvailability(selectedProductForAvailability.id)
              : null
          }
          isSkuLoading={
            selectedProductForAvailability
              ? stockIssueRequestStore.loadingSkuAvailabilityProductId ===
              selectedProductForAvailability.id
              : false
          }
          resolveWarehouseName={resolveWarehouseName}
        />

        <ConfirmationPopup
          id={deleteUi.productPendingDelete?.id || 0}
          open={Boolean(deleteUi.productPendingDelete)}
          title="Delete product"
          content={`Are you sure you want to delete "${deleteUi.productPendingDelete?.name || "this product"}" from this order?`}
          onConfirm={() => {
            void handleConfirmDeleteProduct();
          }}
          onCancel={handleCancelDeleteProduct}
        />

        <Dialog
          open={Boolean(deleteUi.productDeleteBlock)}
          onClose={() => {
            deleteUi.productDeleteBlock = null;
          }}
        >
          <DialogTitle sx={{ fontSize: 18, fontWeight: 500 }}>
            Cannot delete product
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              component="div"
              sx={{ fontSize: 14, display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              {(deleteUi.productDeleteBlock?.pending_purchase_requests.length || 0) >
                0 && (
                <span>
                  This product cannot be deleted because{" "}
                  {(deleteUi.productDeleteBlock?.pending_purchase_requests.length || 0) ===
                  1
                    ? "purchase request "
                    : "purchase requests "}
                  <LinkedIdList
                    items={deleteUi.productDeleteBlock!.pending_purchase_requests}
                    orderId={order_id}
                  />
                  {(deleteUi.productDeleteBlock?.pending_purchase_requests.length || 0) ===
                  1
                    ? " is still pending. Approve or reject that purchase request first."
                    : " are still pending. Approve or reject those purchase requests first."}
                </span>
              )}
              {(deleteUi.productDeleteBlock?.unclosed_purchase_orders.length || 0) >
                0 && (
                <span>
                  This product cannot be deleted because{" "}
                  {(deleteUi.productDeleteBlock?.unclosed_purchase_orders.length || 0) ===
                  1
                    ? "purchase order "
                    : "purchase orders "}
                  <LinkedIdList
                    items={deleteUi.productDeleteBlock!.unclosed_purchase_orders.map(
                      (po: OrderProductDeleteCheckPo) => ({
                        id: po.id,
                        prId: po.pr_id,
                      }),
                    )}
                    orderId={order_id}
                  />
                  {(deleteUi.productDeleteBlock?.unclosed_purchase_orders.length || 0) ===
                  1
                    ? " is not closed. Close that purchase order first."
                    : " are not closed. Close those purchase orders first."}
                </span>
              )}
              {(deleteUi.productDeleteBlock?.purchase_requests_without_closed_po
                .length || 0) > 0 && (
                <span>
                  This product cannot be deleted because{" "}
                  {(deleteUi.productDeleteBlock?.purchase_requests_without_closed_po
                    .length || 0) === 1
                    ? "purchase request "
                    : "purchase requests "}
                  <LinkedIdList
                    items={
                      deleteUi.productDeleteBlock!.purchase_requests_without_closed_po
                    }
                    orderId={order_id}
                  />
                  {(deleteUi.productDeleteBlock?.purchase_requests_without_closed_po
                    .length || 0) === 1
                    ? " does not have a closed purchase order."
                    : " do not have a closed purchase order."}
                </span>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ fontSize: 12 }}
              onClick={() => {
                deleteUi.productDeleteBlock = null;
              }}
              color="primary"
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Box>
  );
};

export default observer(OrderProducts);
